import { useEffect, useState } from 'react';
import { ZodError } from 'zod';
import DashboardComponent from '../../components/Dashboard';
import { User } from '../../components/Dashboard/types';
import EditMyProfile from '../../components/MyProfile/EditProfile';
import MyProfileDetails from '../../components/MyProfile/ProfileDetails';
import getAPIUrl from '../../config';
import { getApi, putApiWithAuth } from '../../utils/apiCalls';
import getLocalStorageData, { setLocalStorageData } from '../../utils/token';
import { UserData } from '../../components/MyProfile/ProfileDetails/types';
import { Roles } from '../../components/MyProfile/EditProfile/types';
import editProfileSchema from '../../utils/schemas';
import { useToast } from '../../components/common/ToasterContext';
import { trimFormData } from '../../utils/helpers';

const Dashboard = () => {
    const [graphData, setGraphData] = useState<[]>([]);
    const [adminCount, setAdminCount] = useState<number>(0);
    const [memberCount, setMemberCount] = useState<number>(0);
    const [partnerCount, setPartnerCount] = useState<number>(0);
    const [contentCount, setContentCount] = useState<number>(0);
    const [openProfileDetails, setOpenProfileDetails] = useState(false);
    const [editProfileDetails, setEditProfileDetails] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const userData = getLocalStorageData();
    const [roles, setRoles] = useState<Roles[]>([]);
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [selectedImage, setSelectedImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedFile, setSelectedFile] = useState<File>();
    const toast = useToast();
    const cards = [
        { title: 'Admins', subTitle: 'Total active', icon: 'users.svg', activeCount: adminCount },
        { title: 'Partners', subTitle: 'Total active', icon: 'partners.svg', activeCount: partnerCount },
        { title: 'Members', subTitle: 'Total active', icon: 'members.svg', activeCount: memberCount },
        { title: 'Content', subTitle: 'Total active', icon: 'content.svg', activeCount: contentCount },
    ];
    const [userInfo, setUserInfo] = useState<UserData>({
        ...userData,
        password: '',
        repeatPassword: '',
        roleId: '',
    });

    const user: User = {
        name: `${userData.firstname} ${userData.lastname}`,
        profileImg: userData.signedImageUrl,
    };

    const [loader, setLoader] = useState(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        setLoader(true);
        getApi(`${getAPIUrl()}/admins/dashboard`)
            .then((res) => {
                const dashboardData = res.data.data;
                setGraphData(dashboardData.activeMembersData);
                setAdminCount(dashboardData.adminCount);
                setMemberCount(dashboardData.memberCount);
                setPartnerCount(dashboardData.partnerCount);
                setContentCount(dashboardData.resourcesCount);
                setLoader(false);
            })
            .catch((_error) => {
                setLoader(false);
            });
        getApi(`${getAPIUrl()}/admins/admin-roles`).then((res) => {
            const rolesData: Roles[] = res.data.data.data.map((r: { role: string; id: string }) => ({
                name: r.role,
                value: Number(r.id),
            }));
            setRoles(rolesData);
            const roleData = rolesData.find((role: { name: string; value: number }) => role.name === userData.roleName);
            setSelectedRoleId(String(roleData?.value));
            setUserInfo({ ...userInfo, roleId: String(roleData?.value) });
        });
    }, []);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editProfileSchema.parse(trimFormData(userInfo));
            const formData = new FormData();
            formData.append('roleId', selectedRoleId);
            formData.append('firstname', userInfo.firstname);
            formData.append('lastname', userInfo.lastname);
            formData.append('imageFile', selectedFile as any);
            if (userInfo.password) {
                formData.append('password', String(userInfo.password || ''));
            }
            if (userInfo.repeatPassword) {
                formData.append('repeatPassword', String(userInfo.repeatPassword || ''));
            }
            const result = await putApiWithAuth(`${getAPIUrl()}/admins/edit-admin`, formData);
            setFormErrors(undefined);
            setUserInfo({
                ...userInfo,
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
                roleName: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                password: '',
                repeatPassword: '',
            });
            setLocalStorageData({
                ...userData,
                adminRole: {
                    role: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                },
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
            });
            setSelectedRoleId(result.data.data.data.roleId);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setEditProfileDetails(false);
            setOpenProfileDetails(false);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(value);
        }
        setUserInfo({
            ...userInfo,
            [name]: String(value),
        });
    };

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleCloseMyProfile = (value: boolean) => {
        setUserInfo({
            ...userData,
            password: '',
            repeatPassword: '',
            roleId: String(
                roles.find((role: { name: string; value: number }) => role.name === userData.roleName)?.value,
            ),
        });
        setEditProfileDetails(value);
        setFormErrors(undefined);
    };

    return (
        <>
            <DashboardComponent
                cards={cards}
                user={user}
                graphData={graphData}
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                loader={loader}
            />
            <MyProfileDetails
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                setEditProfileDetails={setEditProfileDetails}
                userData={userData}
            />
            <EditMyProfile
                editProfileDetails={editProfileDetails}
                userInfo={userInfo}
                handleInput={handleInput}
                handleSubmit={onSubmit}
                roles={roles}
                selectedRoleId={selectedRoleId}
                handleChangeAvatar={handleChangeAvatar}
                selectedImage={selectedImage}
                formErrors={formErrors}
                handleCloseMyProfile={handleCloseMyProfile}
                submitting={submitting}
            />
        </>
    );
};
export default Dashboard;
